/**
 * Maqueta "Presenta tu reclamo":
 * - marca en el menú lateral sticky la sección visible
 * - drag & drop en las zonas de carga
 * - validación de todos los campos (todos obligatorios, evidencias: al menos una)
 * - habilita el envío con el reCAPTCHA v2
 */
document.addEventListener('DOMContentLoaded', () => {
  const form = document.querySelector('[data-claim-form]') as HTMLFormElement | null
  if (!form) return

  type Field = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

  /* ---------- Menú lateral: sección activa ---------- */
  const sections = Array.from(form.querySelectorAll<HTMLElement>('[data-claim-step]'))
  const navLinks = Array.from(document.querySelectorAll<HTMLAnchorElement>('[data-claim-nav]'))

  const setActive = (id: string) => {
    navLinks.forEach((link) => {
      link.classList.toggle('is-active', link.dataset.claimNav === id)
    })
  }

  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]

      if (visible) setActive((visible.target as HTMLElement).dataset.claimStep!)
    },
    // el header es fixed: se descuenta su alto para que el cambio ocurra al llegar la sección
    { rootMargin: '-120px 0px -55% 0px', threshold: 0 }
  )

  sections.forEach((section) => observer.observe(section))

  navLinks.forEach((link) => {
    link.addEventListener('click', () => setActive(link.dataset.claimNav!))
  })

  /* ---------- Mensajes de error ---------- */
  const ERROR_ATTR = 'data-claim-error'
  const ERROR_CLASS = 'mt-6 text-12 font-500 text-red-6'
  const INVALID_CLASS = '!border-red-5'

  // dónde se cuelga el mensaje: el contenedor del campo (label de los componentes)
  const anchorOf = (field: Field): HTMLElement => {
    return (
      field.closest<HTMLElement>('[data-claim-field], [data-claim-evidence]') ??
      (field.closest('label') as HTMLElement | null) ??
      (field.parentElement as HTMLElement)
    )
  }

  const clearError = (field: Field) => {
    const anchor = anchorOf(field)
    anchor.querySelector(`[${ERROR_ATTR}]`)?.remove()
    const target = field.type === 'file' ? anchor.querySelector('[data-claim-drop]') : field
    target?.classList.remove(INVALID_CLASS)
  }

  const showError = (field: Field, message: string) => {
    const anchor = anchorOf(field)
    if (anchor.querySelector(`[${ERROR_ATTR}]`)) return

    const el = document.createElement('p')
    el.setAttribute(ERROR_ATTR, '')
    el.className = ERROR_CLASS
    el.textContent = message
    anchor.appendChild(el)

    const target = field.type === 'file' ? anchor.querySelector('[data-claim-drop]') : field
    target?.classList.add(INVALID_CLASS)
  }

  /* ---------- Reglas ---------- */
  const MB = 1024 * 1024

  const patterns: Record<string, { test: RegExp; message: string }> = {
    ruc: { test: /^\d{11}$/, message: 'El RUC debe tener 11 dígitos.' },
    documento: { test: /^\d{8,12}$/, message: 'Ingrese un número de documento válido.' },
    telefono: { test: /^\d{9}$/, message: 'Ingrese un número de 9 dígitos.' },
    correo: { test: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/, message: 'Ingrese un correo electrónico válido.' },
  }

  const emptyMessage = (field: Field): string => {
    if (field instanceof HTMLSelectElement) return 'Seleccione una opción.'
    if (field.type === 'file') return 'Debe adjuntar un archivo.'
    if (field.type === 'checkbox') return 'Debe aceptar las políticas de privacidad.'
    if (field.type === 'radio') return 'Seleccione una opción.'
    return 'Este campo es obligatorio.'
  }

  const fileError = (field: HTMLInputElement): string | null => {
    const file = field.files?.[0]
    if (!file) return field.required ? emptyMessage(field) : null

    const max = Number(field.dataset.claimMaxsize ?? 5)
    if (file.size > max * MB) return `El archivo supera los ${max}MB.`
    return null
  }

  const validateField = (field: Field): boolean => {
    clearError(field)

    if (field instanceof HTMLInputElement && field.type === 'file') {
      const error = fileError(field)
      if (error) {
        showError(field, error)
        return false
      }
      return true
    }

    if (field instanceof HTMLInputElement && field.type === 'checkbox') {
      if (!field.checked) {
        showError(field, emptyMessage(field))
        return false
      }
      return true
    }

    if (field instanceof HTMLInputElement && field.type === 'radio') {
      const checked = form.querySelector(`input[name="${field.name}"]:checked`)
      if (!checked) {
        showError(field, emptyMessage(field))
        return false
      }
      return true
    }

    const value = field.value.trim()
    if (!value) {
      showError(field, emptyMessage(field))
      return false
    }

    const rule = patterns[field.name]
    if (rule && !rule.test.test(value)) {
      showError(field, rule.message)
      return false
    }

    return true
  }

  /* ---------- Evidencias: al menos una ---------- */
  const evidenceGroup = form.querySelector<HTMLElement>('[data-claim-evidence]')
  const evidenceInputs = Array.from(
    evidenceGroup?.querySelectorAll<HTMLInputElement>('input[type="file"]') ?? []
  )

  const validateEvidence = (): boolean => {
    if (!evidenceGroup || !evidenceInputs.length) return true

    evidenceGroup.querySelector(`[${ERROR_ATTR}]`)?.remove()

    // primero el tamaño de las que sí se cargaron
    let valid = true
    evidenceInputs.forEach((input) => {
      if (!validateField(input)) valid = false
    })
    if (!valid) return false

    const loaded = evidenceInputs.some((input) => input.files?.length)
    if (!loaded) {
      showError(evidenceInputs[0], 'Adjunte al menos una evidencia.')
      return false
    }

    return true
  }

  /* ---------- Validación completa ---------- */
  const requiredFields = () =>
    Array.from(form.querySelectorAll<Field>('[required]')).filter(
      (field) => !evidenceInputs.includes(field as HTMLInputElement)
    )

  const validateForm = (): boolean => {
    // se validan todos para que el usuario vea de una vez qué falta
    const results = requiredFields().map((field) => validateField(field))
    const evidenceOk = validateEvidence()
    const captchaOk = validateCaptcha()

    return results.every(Boolean) && evidenceOk && captchaOk
  }

  const focusFirstError = () => {
    const first = form.querySelector(`[${ERROR_ATTR}]`)
    if (!first) return

    first.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const field = first.parentElement?.querySelector<Field>('input, select, textarea')
    if (field && field.type !== 'file') field.focus({ preventScroll: true })
  }

  /* ---------- Limpieza en vivo ---------- */
  form.querySelectorAll<Field>('input, select, textarea').forEach((field) => {
    const event = field instanceof HTMLSelectElement || field.type === 'file' || field.type === 'checkbox' || field.type === 'radio'
      ? 'change'
      : 'input'

    field.addEventListener(event, () => {
      clearError(field)
      if (evidenceInputs.includes(field as HTMLInputElement)) {
        evidenceGroup?.querySelector(`[${ERROR_ATTR}]`)?.remove()
      }
    })
  })

  // sólo dígitos donde corresponde
  const digitsOnly: Record<string, number> = { ruc: 11, telefono: 9, documento: 12 }
  Object.entries(digitsOnly).forEach(([name, max]) => {
    const input = form.querySelector<HTMLInputElement>(`input[name="${name}"]`)
    input?.addEventListener('input', () => {
      input.value = input.value.replace(/\D/g, '').slice(0, max)
    })
  })

  /* ---------- Zonas de carga ---------- */
  const formatSize = (bytes: number) => `${(bytes / MB).toFixed(2)} MB`

  form.querySelectorAll<HTMLElement>('[data-claim-drop]').forEach((zone) => {
    const input = zone.querySelector<HTMLInputElement>('input[type="file"]')
    const output = zone.querySelector<HTMLElement>('[data-claim-drop-file]')
    if (!input || !output) return

    const showFile = () => {
      const file = input.files?.[0]
      output.classList.toggle('is-visible', Boolean(file))
      output.textContent = file ? `${file.name} · ${formatSize(file.size)}` : ''
      validateField(input)
    }

    input.addEventListener('change', showFile)

    zone.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        input.click()
      }
    })

    ;['dragenter', 'dragover'].forEach((type) => {
      zone.addEventListener(type, (event) => {
        event.preventDefault()
        zone.classList.add('is-dragover')
      })
    })

    ;['dragleave', 'drop'].forEach((type) => {
      zone.addEventListener(type, (event) => {
        event.preventDefault()
        zone.classList.remove('is-dragover')
      })
    })

    zone.addEventListener('drop', (event) => {
      const dropped = (event as DragEvent).dataTransfer?.files
      if (!dropped?.length) return

      const transfer = new DataTransfer()
      transfer.items.add(dropped[0])
      input.files = transfer.files
      showFile()
    })
  })

  /* ---------- reCAPTCHA v2 ---------- */
  const captcha = form.querySelector<HTMLElement>('.g-recaptcha')
  const submit = form.querySelector<HTMLButtonElement>('[data-claim-submit]')

  // función declarada (hoisted): validateForm la usa más arriba
  function validateCaptcha(): boolean {
    if (!captcha) return true

    captcha.parentElement?.querySelector(`[${ERROR_ATTR}]`)?.remove()

    const response = window.grecaptcha?.getResponse() ?? ''
    if (response) return true

    const el = document.createElement('p')
    el.setAttribute(ERROR_ATTR, '')
    el.className = ERROR_CLASS
    el.textContent = 'Complete la verificación "No soy un robot".'
    captcha.parentElement?.insertBefore(el, captcha.nextSibling)
    return false
  }

  // callbacks que invoca el widget de Google (data-callback / data-expired-callback)
  window.claimCaptchaSolved = () => {
    if (submit) submit.disabled = false
    captcha?.parentElement?.querySelector(`[${ERROR_ATTR}]`)?.remove()
  }

  window.claimCaptchaExpired = () => {
    if (submit) submit.disabled = true
  }

  /* ---------- Envío por REST API de CF7 ---------- */
  const status = form.querySelector<HTMLElement>('[data-claim-status]')

  const STATUS_OK = 'bg-green-50 text-green-800'
  const STATUS_ERROR = 'bg-red-50 text-red-700'

  const showStatus = (message: string, ok: boolean) => {
    if (!status) return
    status.classList.remove(...STATUS_OK.split(' '), ...STATUS_ERROR.split(' '))
    status.classList.add(...(ok ? STATUS_OK : STATUS_ERROR).split(' '))
    status.classList.add('is-visible')
    status.textContent = message
  }

  const hideStatus = () => status?.classList.remove('is-visible')

  // CF7 responde con el name del campo rechazado en el servidor
  const showServerErrors = (fields: { field: string; message: string }[] = []) => {
    fields.forEach(({ field, message }) => {
      const input = form.querySelector<Field>(`[name="${field}"]`)
      if (input) showError(input, message)
    })
  }

  const setLoading = (loading: boolean) => {
    if (!submit) return
    submit.textContent = loading ? 'Enviando…' : 'Finalizar'
    // al terminar sólo se rehabilita si el captcha sigue resuelto
    submit.disabled = loading || !(window.grecaptcha?.getResponse() ?? '')
  }

  form.addEventListener('submit', async (event) => {
    event.preventDefault()
    hideStatus()

    if (!validateForm()) {
      focusFirstError()
      return
    }

    const endpoint = form.getAttribute('action')
    if (!endpoint) {
      showStatus('No hay un formulario configurado para recibir el envío.', false)
      return
    }

    const data = new FormData(form)
    data.append('_wpcf7', form.dataset.claimId ?? '')
    data.append('_wpcf7_version', form.dataset.claimVersion ?? '')
    data.append('_wpcf7_locale', form.dataset.claimLocale ?? '')
    data.append('_wpcf7_unit_tag', form.dataset.claimUnit ?? '')

    // el plugin de reCAPTCHA lo espera con este nombre exacto
    const captchaResponse = window.grecaptcha?.getResponse() ?? ''
    if (captchaResponse) data.set('g-recaptcha-response', captchaResponse)

    setLoading(true)

    try {
      const response = await fetch(endpoint, { method: 'POST', body: data })
      const result = await response.json()

      switch (result.status) {
        case 'mail_sent': {
          // el servidor devuelve el correlativo asignado a este envío
          const expedient = result.expediente as string | undefined

          showStatus(
            expedient
              ? `Su reclamo fue registrado con el expediente ${expedient}. Le enviamos la confirmación a su correo.`
              : result.message || 'Su reclamo fue enviado correctamente.',
            true
          )

          if (expedient) {
            const label = document.querySelector<HTMLElement>('[data-claim-expedient]')
            if (label) label.textContent = `Expediente ${expedient}`
          }

          form.reset()
          form.querySelectorAll('[data-claim-drop-file]').forEach((el) => {
            el.classList.remove('is-visible')
            el.textContent = ''
          })
          window.grecaptcha?.reset() // vuelve a exigir el captcha
          status?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          break
        }

        case 'validation_failed':
          showServerErrors(result.invalid_fields)
          showStatus(result.message || 'Revise los campos marcados.', false)
          focusFirstError()
          break

        default:
          // spam, aborted, mail_failed
          showStatus(result.message || 'No se pudo enviar el reclamo. Intente nuevamente.', false)
          window.grecaptcha?.reset()
      }
    } catch {
      showStatus('Error de conexión. Verifique su red e intente nuevamente.', false)
    } finally {
      setLoading(false)
    }
  })
})
